import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

/**
 * QueryLogsTable Component
 *
 * Displays query logs in a table format with:
 * - Expandable rows to show full query text
 * - Status badges
 * - Execution time formatting
 * - Copy query button
 * - Sortable columns
 */
const QueryLogsTable = ({ logs = [], loading = false }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const copyQuery = (queryText, id) => {
    navigator.clipboard.writeText(queryText);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { bg: 'bg-green-100', text: 'text-green-800', label: 'Success' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      timeout: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Timeout' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.success;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (ms == null) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const truncateQuery = (query, maxLength = 80) => {
    if (!query) return 'N/A';
    if (query.length <= maxLength) return query;
    return query.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading query logs...</span>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500">No query logs found for the selected filters.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-8 px-3 py-3.5"></th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Executed At
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Connection
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Query
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Type
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Automation
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Duration
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Rows
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Status
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {logs.map((log) => (
            <React.Fragment key={log.id}>
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-4 text-center">
                  <button
                    onClick={() => toggleRow(log.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {expandedRow === log.id ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                </td>
                <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {formatDate(log.executed_at)}
                </td>
                <td className="px-3 py-4 text-sm text-gray-900">
                  {log.connection_name || 'Unknown'}
                </td>
                <td className="px-3 py-4 text-sm text-gray-700 font-mono max-w-md">
                  {truncateQuery(log.query_text)}
                </td>
                <td className="px-3 py-4 text-sm text-gray-600 whitespace-nowrap">
                  {log.query_type || 'N/A'}
                </td>
                <td className="px-3 py-4 text-sm text-gray-600 whitespace-nowrap">
                  {log.automation_type || 'manual'}
                </td>
                <td className="px-3 py-4 text-sm text-gray-600 whitespace-nowrap">
                  {formatDuration(log.execution_time_ms)}
                </td>
                <td className="px-3 py-4 text-sm text-gray-600 whitespace-nowrap">
                  {log.rows_scanned != null ? log.rows_scanned.toLocaleString() : 'N/A'}
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap">
                  {getStatusBadge(log.status)}
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap">
                  <button
                    onClick={() => copyQuery(log.query_text, log.id)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1"
                    title="Copy query"
                  >
                    {copiedId === log.id ? (
                      <>
                        <CheckIcon className="h-4 w-4" />
                        <span className="text-xs">Copied</span>
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        <span className="text-xs">Copy</span>
                      </>
                    )}
                  </button>
                </td>
              </tr>

              {/* Expanded row showing full query */}
              {expandedRow === log.id && (
                <tr>
                  <td colSpan="10" className="px-3 py-4 bg-gray-50">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          Full Query
                        </h4>
                        <pre className="bg-white border border-gray-200 rounded p-3 text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto">
                          {log.query_text}
                        </pre>
                      </div>

                      {log.error_message && (
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Error Details
                          </h4>
                          <div className="bg-red-50 border border-red-200 rounded p-3">
                            <p className="text-sm text-red-900">
                              <span className="font-semibold">Code:</span> {log.error_code || 'N/A'}
                            </p>
                            <p className="text-sm text-red-800 mt-1">
                              {log.error_message}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {log.rows_affected != null && (
                          <div>
                            <span className="text-gray-500">Rows Affected:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {log.rows_affected.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {log.bytes_scanned != null && (
                          <div>
                            <span className="text-gray-500">Bytes Scanned:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {(log.bytes_scanned / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        )}
                        {log.credits_used != null && (
                          <div>
                            <span className="text-gray-500">Credits Used:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {log.credits_used.toFixed(6)}
                            </span>
                          </div>
                        )}
                        {log.warehouse_name && (
                          <div>
                            <span className="text-gray-500">Warehouse:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {log.warehouse_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default QueryLogsTable;
